import { useEffect, useMemo, useRef, useState } from "react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { Product } from "../../appData/productData"
import { formatCurrency } from "../../utils/utils"
import styles from "./KindredPaywall.module.scss"

/**
 * KindredPaywall — an original, self-contained, NON-privacy-fear paywall.
 *
 * Divergence brief (see claude-paywalls/_ledger.md): the prior paywalls
 * clustered on PRIVACY pitched as DARK + SKEUOMORPHIC, on a centered-hero +
 * one-glowing-card skeleton, mostly passive. Kindred moves every axis:
 *   - Value frame:   SHARING — bringing the people you love into one private
 *                    space. (Untouched by every prior paywall.)
 *   - Layout:        a CHAT / MESSAGES THREAD (receipt-or-chat archetype) —
 *                    no hero, no glowing card. The screen is a conversation.
 *   - Interaction:   INPUT-DRIVEN but a new sub-variant — you TAP faces to
 *                    invite them into your circle and the thread comes alive
 *                    with their reactions (typing indicators and all).
 *   - Register:      COZY / warm — family, not fear.
 *   - Art direction: WARM LIGHT, soft, with PHOTOGRAPHIC album covers inside
 *                    the bubbles. Not dark, not skeuomorphic-metal.
 *
 * The pitch is made tangible: an empty circle feels lonely; adding people
 * fills the thread with warmth. Yearly is the prominent price.
 */

// Real, warm content imagery (Unsplash) for the shared album covers.
const ALBUM_PHOTOS = [
  "https://images.unsplash.com/photo-1528256507509-cc4facea43f6?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=400",
  "https://images.unsplash.com/photo-1556870569-ca98f8a42917?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=400",
  "https://images.unsplash.com/photo-1725573839625-0a1ddd342964?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=400",
  "https://images.unsplash.com/photo-1554704674-8d3bd5e18b55?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=400",
]
const RESHARE_PHOTO =
  "https://images.unsplash.com/photo-1761339014631-6cab67069644?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=400"

interface Member {
  id: string
  name: string
  initials: string
  color: string
  reaction: string
  reshare?: boolean
}

const MEMBERS: Member[] = [
  {
    id: "mom",
    name: "Mom",
    initials: "M",
    color: "#ff7a59",
    reaction: "Oh my heart 😭 that sunset! I'm printing this one for the hall.",
  },
  {
    id: "dad",
    name: "Dad",
    initials: "D",
    color: "#2bb5a0",
    reaction: "Saved every single one. Full quality, look at that 👏",
  },
  {
    id: "lily",
    name: "Lily",
    initials: "L",
    color: "#8b6dff",
    reaction: "Adding mine from the hike 🥾",
    reshare: true,
  },
  {
    id: "theo",
    name: "Theo",
    initials: "T",
    color: "#4cae7a",
    reaction: "Best trip ever. When's the next one?? 🌊",
  },
  {
    id: "nana",
    name: "Nana",
    initials: "N",
    color: "#e26d9b",
    reaction: "I can see them so clearly now. Love you all ❤️",
  },
]

const KindredPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")
  const [invited, setInvited] = useState<string[]>([])
  const [typing, setTyping] = useState<string[]>([])
  const timers = useRef<ReturnType<typeof setTimeout>[]>([])
  const threadEndRef = useRef<HTMLDivElement>(null)

  const invite = (id: string) => {
    if (invited.includes(id) || typing.includes(id)) return
    if (reduceMotion) {
      setInvited((prev) => [...prev, id])
      return
    }
    setTyping((prev) => [...prev, id])
    const t = setTimeout(() => {
      setTyping((prev) => prev.filter((x) => x !== id))
      setInvited((prev) => [...prev, id])
    }, 1050)
    timers.current.push(t)
  }

  // One-time auto-demo: invite Mom so the mechanic announces itself, and the
  // thread is never sitting empty.
  const demoedRef = useRef(false)
  useEffect(() => {
    if (demoedRef.current) return
    demoedRef.current = true
    if (reduceMotion) {
      setInvited(["mom"])
      return
    }
    const t = setTimeout(() => invite("mom"), 750)
    timers.current.push(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  useEffect(() => {
    const list = timers.current
    return () => list.forEach(clearTimeout)
  }, [])

  // Keep the latest reaction in view as the circle grows.
  useEffect(() => {
    if (reduceMotion) return
    threadEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" })
  }, [invited, typing, reduceMotion])

  const remaining = MEMBERS.filter(
    (m) => !invited.includes(m.id) && !typing.includes(m.id),
  )
  const count = invited.length

  const sharedLabel = useMemo(() => {
    if (count === 0) return "Right now, only you can see this"
    if (count === 1) return "Shared with 1 person in your circle"
    return `Shared with ${count} people in your circle`
  }, [count])

  const handleClose = () => native.postNativeEvent("close")
  const handlePurchase = () => {
    const product: Product | undefined =
      plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  const selectedHasTrial =
    plan === "yearly"
      ? (yearlyProduct?.hasTrial ?? true)
      : (monthlyProduct?.hasTrial ?? false)

  const invitedMembers = invited
    .map((id) => MEMBERS.find((m) => m.id === id)!)
    .filter(Boolean)

  return (
    <div className={styles.page}>
      {/* ── Chat header ─────────────────────────────────────────── */}
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.stack}>
            <span className={styles.you} aria-hidden>
              You
            </span>
            <AnimatePresence>
              {invitedMembers.slice(0, 4).map((m, i) => (
                <motion.span
                  key={m.id}
                  className={styles.stackAvatar}
                  style={{ background: m.color, zIndex: 10 + i }}
                  initial={
                    reduceMotion ? false : { scale: 0, x: -8, opacity: 0 }
                  }
                  animate={{ scale: 1, x: 0, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 420, damping: 22 }}
                  aria-hidden
                >
                  {m.initials}
                </motion.span>
              ))}
            </AnimatePresence>
          </div>
          <div className={styles.headerText}>
            <span className={styles.headerTitle}>Family Circle</span>
            <span className={styles.headerSub}>
              <svg viewBox="0 0 24 24" width="11" height="11" aria-hidden>
                <path
                  d="M6 10V8a6 6 0 1 1 12 0v2M5 10h14v9a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2z"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {count === 0
                ? "Private to you"
                : `${count + 1} ${count === 0 ? "person" : "people"} · private`}
            </span>
          </div>
        </div>
        <button
          className={styles.close}
          onClick={handleClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2.3"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      {/* ── The thread ──────────────────────────────────────────── */}
      <main className={styles.thread}>
        <div className={styles.dayDivider}>
          <span>Today</span>
        </div>

        <div className={styles.systemNote}>
          This is your private circle. Only the people you invite can ever see
          what you share here.
        </div>

        {/* Your outgoing share */}
        <div className={`${styles.row} ${styles.rowOut}`}>
          <div className={styles.bubbleOut}>
            <div className={styles.albumCard}>
              <div className={styles.albumGrid}>
                {ALBUM_PHOTOS.map((src, i) => (
                  <div key={i} className={styles.albumCell}>
                    <img src={src} alt="" loading="lazy" />
                    {i === 3 && <span className={styles.albumMore}>+244</span>}
                  </div>
                ))}
              </div>
              <div className={styles.albumMeta}>
                <span className={styles.albumName}>📁 Maui &rsquo;25</span>
                <span className={styles.albumCountText}>248 photos · 4K</span>
              </div>
            </div>
            <p className={styles.bubbleCaption}>Wish you were all here ☀️</p>
            <span className={styles.sharedTag}>{sharedLabel}</span>
          </div>
        </div>

        {/* Incoming reactions, as people are invited */}
        <AnimatePresence initial={false}>
          {invitedMembers.map((m) => (
            <motion.div
              key={m.id}
              className={`${styles.row} ${styles.rowIn}`}
              initial={
                reduceMotion ? false : { opacity: 0, y: 14, scale: 0.96 }
              }
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ type: "spring", stiffness: 360, damping: 26 }}
            >
              <span
                className={styles.avatar}
                style={{ background: m.color }}
                aria-hidden
              >
                {m.initials}
              </span>
              <div className={styles.bubbleInWrap}>
                <span className={styles.senderName}>{m.name}</span>
                <div className={styles.bubbleIn}>
                  {m.reshare && (
                    <div className={styles.reshare}>
                      <img src={RESHARE_PHOTO} alt="" loading="lazy" />
                      <span>added 3 photos</span>
                    </div>
                  )}
                  {m.reaction}
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Typing indicators */}
        <AnimatePresence>
          {typing.map((id) => {
            const m = MEMBERS.find((x) => x.id === id)!
            return (
              <motion.div
                key={`typing-${id}`}
                className={`${styles.row} ${styles.rowIn}`}
                initial={reduceMotion ? false : { opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
              >
                <span
                  className={styles.avatar}
                  style={{ background: m.color }}
                  aria-hidden
                >
                  {m.initials}
                </span>
                <div
                  className={styles.typing}
                  aria-label={`${m.name} is typing`}
                >
                  <i /> <i /> <i />
                </div>
              </motion.div>
            )
          })}
        </AnimatePresence>

        <div ref={threadEndRef} />
      </main>

      {/* ── Invite composer (the interactive mechanic) ──────────── */}
      <section className={styles.composer}>
        <p className={styles.composerLabel}>
          {remaining.length > 0
            ? "Tap to bring them into the circle"
            : "Your whole circle is here ❤️"}
        </p>
        <div className={styles.faces}>
          <AnimatePresence initial={false}>
            {remaining.map((m) => (
              <motion.button
                key={m.id}
                type="button"
                className={styles.faceBtn}
                onClick={() => invite(m.id)}
                initial={false}
                exit={reduceMotion ? undefined : { scale: 0, opacity: 0 }}
                whileTap={reduceMotion ? undefined : { scale: 0.9 }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              >
                <span
                  className={styles.face}
                  style={{ background: m.color }}
                  aria-hidden
                >
                  {m.initials}
                  <span className={styles.plus}>+</span>
                </span>
                <span className={styles.faceName}>{m.name}</span>
              </motion.button>
            ))}
          </AnimatePresence>
          {remaining.length === 0 && (
            <span className={styles.allIn}>
              Everyone you love, in one private place.
            </span>
          )}
        </div>
      </section>

      {/* ── Offer ───────────────────────────────────────────────── */}
      <section className={styles.offer}>
        <h1 className={styles.offerTitle}>
          Sharing is a <span className={styles.warm}>Premium</span> thing.
        </h1>
        <p className={styles.offerSub}>
          Invite the people who matter, share whole albums at full quality, and
          keep it all in one space only your circle can see.
        </p>

        <div className={styles.plans}>
          <button
            type="button"
            className={`${styles.plan} ${plan === "yearly" ? styles.planOn : ""}`}
            onClick={() => setPlan("yearly")}
          >
            <span className={styles.planRadio} aria-hidden />
            <span className={styles.planBody}>
              <span className={styles.planTop}>
                <span className={styles.planName}>Yearly</span>
                <span className={styles.saveTag}>Best for families</span>
              </span>
              <span className={styles.planNote}>
                {yearlyPerMonth}/mo · billed annually
              </span>
            </span>
            <span className={styles.planPriceWrap}>
              <span className={styles.planPrice}>{yearlyPrice}</span>
              <span className={styles.planUnit}>/year</span>
            </span>
          </button>

          <button
            type="button"
            className={`${styles.planSmall} ${plan === "monthly" ? styles.planSmallOn : ""}`}
            onClick={() => setPlan("monthly")}
          >
            <span className={styles.planRadioSmall} aria-hidden />
            <span className={styles.planNameSmall}>Monthly</span>
            <span className={styles.planPriceSmall}>{monthlyPrice}/mo</span>
          </button>
        </div>

        <button className={styles.cta} onClick={handlePurchase}>
          {selectedHasTrial
            ? "Start your circle — free for 7 days"
            : "Start your circle"}
        </button>
        <p className={styles.ctaSub}>
          {plan === "yearly"
            ? selectedHasTrial
              ? `7 days free, then ${yearlyPrice}/year. Cancel anytime.`
              : `${yearlyPrice}/year · just ${yearlyPerMonth}/mo. Cancel anytime.`
            : `${monthlyPrice}/month. Cancel anytime.`}
        </p>

        <p className={styles.reassure}>
          You choose exactly who's in. Everything stays private to your circle,
          at full resolution — yours to keep, always.
        </p>

        <footer className={styles.footer}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </footer>
      </section>
    </div>
  )
}

export default KindredPaywall
